import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { cloneDeep, toPlainObject } from 'lodash/fp';
import { firstValueFrom } from 'rxjs';
import { CustomPlayerInputComponent } from '../custom-player-input/custom-player-input.component';

import {
  AggregationStatus,
  CVPipelineStatus,
  DataSet,
  FinalizationStatus,
  Game,
  GameOfficial,
  GameOfficialInputField,
  GameProgress,
  GameStatus,
  IdType,
  OfficialRoleInGame,
  OfficiatingDataSet,
  SeasonType,
  SihfGameStatus,
  Video,
  VideoClipsStatus
} from '../domain/game';
import { League } from '../domain/league';
import { Official } from '../domain/official';
import { Player } from '../domain/player';
import { Team } from '../domain/team';
import { CollectionOption } from '../game-collection-option/game-collection-option.model';
import { GameCollectionOptionService } from '../game-collection-option/game-collection-option.service';
import { AlertService } from '../services/alert.service';
import { deepDiffObj } from '../services/deep-diff-obj';
import { EventService } from '../services/event.service';
import { GameTimeService } from '../services/game-time.service';
import { GameService } from '../services/game.service';
import { GameVideoDetailsService } from '../services/game-video-details.service';
import { LeagueService } from '../services/league.service';
import { OfficialService } from '../services/official.service';
import { PlayerService } from '../services/player.service';
import { SeasonService } from '../services/season.service';
import { TeamService } from '../services/team.service';
import { VideoService } from '../services/video.service';
import { PLAYER_ID_REGEXP } from '../shared/playerid-validator.directive';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { DcRosterComponent } from './dc-roster/dc-roster.component';
import { GameDetailsEssentialsCardComponent } from './game-details-essentials-card/game-details-essentials-card.component';
import { AuthService } from '../auth/auth.service';

type TeamType = 'home' | 'away';
const GAME_AND_DC_ROSTER_SUBTYPE_REQUIRED_WARNING =
  'Game and DC roster changes were not saved because DC Subtype is required when a DC Type is selected.';

@Component({
  selector: 'app-game-details',
  templateUrl: './game-details.component.html',
  styleUrls: ['./game-details.component.css']
})
export class GameDetailsComponent implements OnInit, AfterContentChecked {
  gameStatusOptions = Object.values(GameStatus);
  gameProgressOptions = Object.values(GameProgress);
  aggregationStatusOptions = Object.values(AggregationStatus);
  finalizationStatusOptions = Object.values(FinalizationStatus);
  videoClipsStatusOptions = Object.values(VideoClipsStatus);
  readonly dataSets = Object.values(DataSet);
  readonly officiatingDataSets = Object.values(OfficiatingDataSet);

  sihfGameStatus = SihfGameStatus;
  sihfGameStatusKeys = Object.keys(SihfGameStatus)
    .filter((k) => !isNaN(Number(k)))
    .map((k) => +k);

  seasons: number[] = [];
  currentSeason: number;
  leagues: League[] = [];
  game: Game = new Game({});
  gameUnmodified: Game;
  teams: Team[] = [];
  private prevEnableExtraLines: boolean;

  @ViewChildren(CustomPlayerInputComponent)
  customPlayerInputs: QueryList<CustomPlayerInputComponent>;
  @ViewChild(DcRosterComponent)
  dcRosterComponent?: DcRosterComponent;
  @ViewChild(GameDetailsEssentialsCardComponent)
  gameDetailsEssentialsComponent?: GameDetailsEssentialsCardComponent;

  readonly lines = [
    'FirstLine',
    'SecondLine',
    'ThirdLine',
    'FourthLine',
    'FifthLine',
    'SixthLine',
    'SeventhLine',
    'EighthLine'
  ];

  readonly linePositions = [
    'LeftDefence',
    'RightDefence',
    'LeftForward',
    'Center',
    'RightForward'
  ];

  get visibleLines() {
    return this.game?.enableExtraLines ? this.lines : this.lines.slice(0, 5);
  }

  collectionOptionChanged(option: CollectionOption, value: boolean) {
    if (option.id !== 'enable_extra_lines') {
      return;
    }
    const turnedOff = this.prevEnableExtraLines && !value;
    this.prevEnableExtraLines = !!value;
    if (!turnedOff) {
      return;
    }

    const ref = this.dialog.open(ConfirmationDialogComponent, {
      width: '480px',
      data: {
        title: 'Disable Extra Lines',
        message:
          'This will clear all lineup fields on lines 6–8 for both teams. Are you sure?',
        okButtonCaption: 'Remove'
      }
    });

    ref.afterClosed().subscribe(async (ok) => {
      if (ok) {
        this.clearExtraLineup();
        await this.apply();
      } else {
        this.game.enableExtraLines = true;
        this.prevEnableExtraLines = true;
      }
    });
  }

  private clearExtraLineup() {
    const sides = ['home', 'away'];
    const lines = ['Sixth', 'Seventh', 'Eighth'];
    for (const side of sides) {
      for (const line of lines) {
        for (const pos of this.linePositions) {
          const base = `${side}${line}Line${pos}` as any;
          (this.game as any)[base] = '';
          (this.game as any)[`${base}ID`] = '';
        }
      }
    }
  }
  readonly gameOfficialFields: GameOfficialInputField[] = [
    { label: 'Head Referee 1', role: 'head', sequenceNumber: 1 },
    { label: 'Head Referee 2', role: 'head', sequenceNumber: 2 },
    { label: 'Linesperson 1', role: 'linesperson', sequenceNumber: 1 },
    { label: 'Linesperson 2', role: 'linesperson', sequenceNumber: 2 }
  ];
  officials: Official[] = [];

  private hasGameIncidentAssignedOfficials = false;

  panelsReady = false;
  showEssential: boolean;
  showCollection: boolean;
  showStatus: boolean;
  showVideos: boolean;
  showOfficials: boolean;
  showLineup: boolean;
  showAssistance: boolean;

  homeTeamPlayers: Player[] = [];
  awayTeamPlayers: Player[] = [];
  customLineup = false;
  showDataCollectorRoster = false;
  isSyncingLiveStreams = false;

  originalHomeLineup: Partial<Game> | null = null;
  originalAwayLineup: Partial<Game> | null = null;
  homeCustomerLineupApplied = false;
  awayCustomerLineupApplied = false;

  constructor(
    private gameService: GameService,
    private leagueService: LeagueService,
    private teamService: TeamService,
    private playerService: PlayerService,
    private officialService: OfficialService,
    private gameTimeService: GameTimeService,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private seasonService: SeasonService,
    private collectionOptionService: GameCollectionOptionService,
    private sanitizer: DomSanitizer,
    private eventService: EventService,
    private gameVideoDetailsService: GameVideoDetailsService,
    private videoService: VideoService,
    public authService: AuthService
  ) {}

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  async ngOnInit() {
    const [seasons, currentSeason] = await Promise.all([
      firstValueFrom(this.seasonService.getSeasons()),
      firstValueFrom(this.seasonService.getCurrentSeason())
    ]);
    this.seasons = seasons;
    this.currentSeason = currentSeason;
    this.game = this.route.snapshot.data['game'];
    this.gameUnmodified = cloneDeep(this.game);
    this.onDataSetChange(this.game.dataSet);
    this.setupPanels();
    if (this.game._id) {
      this.gameTimeService.init(this.game._id);
    } else {
      this.game.season = this.currentSeason.toString();
      this.game.seasonType = SeasonType.REGULAR;
      this.game.idType = IdType.MASTER;
      this.game.dataSet = DataSet.LITE;
    }
    this.gameVideoDetailsService.ensureVideos(this.game);
    if (this.game.league && this.game.season) {
      this.teams = await this.teamService.getTeams(
        this.game.league,
        this.game.season,
        this.game.idType
      );
    }
    // sort teams by fullName, shortName
    this.teams.sort((a, b) => a.fullName.localeCompare(b.fullName));

    if (this.game.homeTeam) {
      const homeTeam = this.findTeamByShortName(this.game.homeTeam);
      if (homeTeam) {
        this.homeTeamPlayers = await this.playerService.getPlayersByTeam(
          homeTeam.id,
          this.game.idType
        );
      }
    }
    if (this.game.awayTeam) {
      const awayTeam = this.findTeamByShortName(this.game.awayTeam);
      if (awayTeam) {
        this.awayTeamPlayers = await this.playerService.getPlayersByTeam(
          awayTeam.id,
          this.game.idType
        );
      }
    }
    this.customLineup =
      this.homeTeamPlayers?.length === 0 && this.awayTeamPlayers?.length === 0;
    this.leagues = await firstValueFrom(this.leagueService.getLeagues());
    this.officials = await this.fetchOfficials(this.game.getOfficialIds());

    await this.checkIncidentOfficials();
    this.prevEnableExtraLines = !!this.game.enableExtraLines;
  }

  private async checkIncidentOfficials() {
    if (!this.game?._id) {
      return;
    }

    try {
      const [_, events] = await firstValueFrom(
        this.eventService.getEvents(
          this.game._id,
          { eventType: 'game_incident' },
          false,
          0,
          1000
        )
      );

      this.hasGameIncidentAssignedOfficials = events.some(
        (e) => (e.assignedOfficialIds ?? []).length > 0
      );
    } catch (_error) {
      // If the request fails, we leave `hasGameIncidentAssignedOfficials`
      // at its default value (false). No further action required.
    }
  }

  getVisibleCollectionGroups(): CollectionOption[][] {
    return this.collectionOptionService.getCollectionGroups(this.game);
  }

  collectionOptionTooltip(option: CollectionOption): string {
    return this.collectionOptionService.tooltip(option, this.game);
  }

  collectionOptions(groupKey: string) {
    const optionIds = this.getVisibleCollectionGroups()[groupKey];
    return this.collectionOptionService.collectionOptions(optionIds ?? []);
  }

  private async fetchOfficials(officialIds: number[]) {
    if (officialIds && officialIds.length > 0) {
      const officials = await firstValueFrom(
        this.officialService.fetchByInternalIds(officialIds)
      );
      return officials.map((official) => ({
        ...official,
        jerseyNumber: this.game.officials.find(
          (gameOfficial) => gameOfficial.officialId === official.id
        )?.jerseyNumber
      }));
    } else {
      return [];
    }
  }

  setupPanels() {
    this.showEssential = this.game.status === GameStatus.NEW;
    this.showCollection = this.game.status === GameStatus.NEW;
    this.showVideos =
      this.game.status === GameStatus.NEW ||
      this.game.progress === GameProgress.ENDED;
    this.showLineup = this.game.status === GameStatus.NEW;
    this.showOfficials = this.game.status === GameStatus.NEW;
    // A candidate game CV has not run on yet is exactly the case the panel's
    // hint is for, so open the panel instead of hiding the hint behind a click.
    this.showAssistance =
      this.game.status === GameStatus.NEW ||
      this.game.cvPipeline?.length > 0 ||
      (this.game.isCVCandidate === true && !this.game.isCVProcessed);
    this.showStatus = true;
    this.showDataCollectorRoster = this.game.status === GameStatus.NEW;
    this.panelsReady = true;
  }

  async onLeagueChange(league: string): Promise<void> {
    this.resetLineUp();
    await this.loadTeams(league, this.game.season);
    this.cleanTeams();
  }

  async onSeasonChange(season: string): Promise<void> {
    this.resetLineUp();
    await this.loadTeams(this.game.league, season);
    this.cleanTeams();
  }

  cleanTeams() {
    delete this.game.homeTeamId;
    delete this.game.homeTeam;
    delete this.game.awayTeamId;
    delete this.game.awayTeam;
  }

  get selectedLeague() {
    return this.leagues.find((l) => l.sihfId === this.game.league);
  }

  async onIdTypeChange(idType: string) {
    const homeTeam = this.findTeamByShortName(this.game.homeTeam);
    const awayTeam = this.findTeamByShortName(this.game.awayTeam);

    await Promise.all([
      this.game.homeTeam ? this.onHomeTeamChange(homeTeam) : Promise.resolve(),
      this.game.awayTeam ? this.onAwayTeamChange(awayTeam) : Promise.resolve()
    ]);
  }

  async onHomeTeamChange(team: Team) {
    if (!team) {
      this.game.homeTeam = null;
      this.game.homeTeamId = null;
      return;
    }
    const homeTeamId =
      team[this.teamService.getFieldNameByIdType(this.game.idType)];
    this.game.homeTeam = team.shortName;
    this.game.homeTeamId = homeTeamId;
    await this.loadHomeTeamPlayers(homeTeamId);
  }

  async onAwayTeamChange(team: Team) {
    if (!team) {
      this.game.awayTeam = null;
      this.game.awayTeamId = null;
      return;
    }
    const awayTeamId =
      team[this.teamService.getFieldNameByIdType(this.game.idType)];
    this.game.awayTeam = team.shortName;
    this.game.awayTeamId = awayTeamId;
    await this.loadAwayTeamPlayers(awayTeamId);
  }

  handlePlayerSelect(player: Player | null, positionKey: string) {
    this.game[positionKey + 'ID'] = player?.id ?? '';
    this.game[positionKey] = this.formatPlayer(player);
  }

  private formatPlayer(player: Player | null) {
    if (!player) {
      return '';
    }
    const jerseyNumber = player.jerseyNumber ? `${player.jerseyNumber} - ` : '';
    return jerseyNumber.concat(`${player.lastName} ${player.firstName}`);
  }

  private findTeamByShortName(teamShortName: string) {
    return this.teams.find((t) => t.shortName === teamShortName);
  }

  private resetLineUp() {
    this.teams = [];
    this.homeTeamPlayers = [];
    this.awayTeamPlayers = [];
  }

  private async loadTeams(leagueId: string, season: string) {
    this.teams = await this.teamService.getTeams(
      leagueId,
      season,
      this.game.idType
    );
  }

  private async loadHomeTeamPlayers(teamId: string) {
    if (teamId != null) {
      this.homeTeamPlayers = await this.playerService.getPlayersByTeam(
        teamId,
        this.game.idType
      );
    }
  }

  private async loadAwayTeamPlayers(teamId: string) {
    if (teamId != null) {
      this.awayTeamPlayers = await this.playerService.getPlayersByTeam(
        teamId,
        this.game.idType
      );
    }
  }

  get isGameNew() {
    return !this.game._id;
  }

  get isOfficialEditDisabled() {
    const gameFinished =
      this.game.status === GameStatus.COMPLETE ||
      this.game.status === GameStatus.QUALITY_CONTROL;

    return gameFinished || this.hasGameIncidentAssignedOfficials;
  }

  get isRecalculatePointsDisabled(): boolean {
    return (
      !this.game._id ||
      this.game.homeGoals == null ||
      this.game.awayGoals == null
    );
  }

  get recalculatePointsTooltip(): string {
    if (!this.game._id) {
      return 'Save the game before recalculating points';
    }
    if (this.game.homeGoals == null || this.game.awayGoals == null) {
      return 'Set the home and away scores first';
    }
    return 'Recompute home/away points from the league\'s point system';
  }

  async apply(): Promise<boolean> {
    try {
      if (!this.gameDetailsEssentialsComponent) {
        throw new Error('Essentials component is not available');
      }
      if (this.dcRosterComponent && !this.dcRosterComponent.isValidForSave()) {
        this.alertService.showWarning(
          GAME_AND_DC_ROSTER_SUBTYPE_REQUIRED_WARNING
        );
        return false;
      }
      this.gameDetailsEssentialsComponent.validateAndSyncVenueData();
      this.checkSihfLiveTrainingGame();
      this.checkPlayerIds();
      this.checkCustomLineup();
      this.checkDuplicates(this.game.getAllPlayers(), 'player');
      this.checkDuplicates(
        this.game
          .getAllPlayersObj()
          .map((p) => p.playerId)
          .filter((playerId) => playerId),
        'player'
      );
      this.checkDuplicates(
        this.game
          .getOfficialIds()
          .map((id) => this.officials.find((o) => o.id === id))
          .filter((o) => !!o)
          .map(
            (o) =>
              `${o.lastName} ${o.firstName} (SIHF-ID: ${o.sihfId}, ID: ${o.id})`
          ),
        'official'
      );
      const heads = this.game.officials.filter(
        (official) => official.officialRoleInGame === 'head'
      );
      const linespersons = this.game.officials.filter(
        (official) => official.officialRoleInGame === 'linesperson'
      );
      const officials = { heads, linespersons };
      Object.keys(officials).forEach((headOrLinesperson) => {
        this.checkDuplicates(
          officials[headOrLinesperson]
            .filter((o) => o?.jerseyNumber != null)
            .map((o) => o.jerseyNumber.toString()),
          `${headOrLinesperson} jersey number`
        );
      });
      this.gameVideoDetailsService.validateVideos(this.game);
    } catch (e) {
      this.alertService.showError('Save game failed: ' + e.message);
      return false;
    }

    const isNewGame = this.isGameNew;
    try {
      if (isNewGame) {
        const createdGame = await firstValueFrom(
          this.gameService.create(this.game)
        );
        if (createdGame) {
          Object.assign(this.game, createdGame);
        }
      } else {
        const changes = deepDiffObj(
          toPlainObject(this.gameUnmodified),
          toPlainObject(this.game)
        );
        const roster = this.dcRosterComponent?.dcRoster;
        let confirmed = false;
        if (
          !this.gameUnmodified.isTrainingGame &&
          this.game.isTrainingGame &&
          roster?._id
        ) {
          confirmed = await this.confirmTrainingDcDeletion();
          if (!confirmed) {
            return false;
          }
        }
        const update = () =>
          firstValueFrom(
            this.gameService.update(this.game._id, {
              ...changes,
              ...(confirmed ? { confirmTrainingDcDeletion: true } : {})
            })
          );
        let updatedGame;
        try {
          updatedGame = await update();
        } catch (error) {
          if (
            confirmed ||
            error?.status !== 409 ||
            error?.error?.code !== 'TRAINING_GAME_CONFIRMATION_REQUIRED'
          ) {
            throw error;
          }
          confirmed = await this.confirmTrainingDcDeletion();
          if (!confirmed) {
            return false;
          }
          updatedGame = await update();
        }
        if (updatedGame) {
          Object.assign(this.game, updatedGame);
        }
        if (!this.gameUnmodified.isTrainingGame && this.game.isTrainingGame) {
          this.dcRosterComponent?.clearRoster();
        }
      }

      this.gameUnmodified = cloneDeep(this.game);
    } catch (error) {
      const action = isNewGame ? 'create' : 'update';
      console.error(`${action} game failed: `, error);
      this.alertService.showError(
        `Could not ${action} game: ` + this.getSaveErrorMessage(error)
      );
      return false;
    }

    try {
      const rosterSaved = this.game.isTrainingGame
        ? true
        : await this.dcRosterComponent?.saveDcRosterIfNeeded(this.game._id, {
            recalculate: true
          });
      if (rosterSaved === false) {
        return false;
      }
    } catch (error) {
      console.error('save DC roster failed: ', error);
      this.alertService.showError(
        'Game saved, but DC roster could not be saved: ' +
          this.getSaveErrorMessage(error)
      );
      return false;
    }

    this.alertService.showInfo('Game saved');
    return true;
  }

  private async confirmTrainingDcDeletion(): Promise<boolean> {
    return (
      (await firstValueFrom(
        this.dialog
          .open(ConfirmationDialogComponent, {
            width: '480px',
            data: {
              title: 'Mark game as training',
              message:
                'This game has a DC roster or DC events. Marking it as training will delete all DC events and its DC roster, ' +
                'and remove the game from Data Cockpit. Are you sure?',
              okButtonCaption: 'Mark as training and delete',
              cancelButtonCaption: 'Cancel'
            }
          })
          .afterClosed()
      )) === true
    );
  }

  async save() {
    const successful = await this.apply();
    if (successful) {
      await this.router.navigate(['games']);
    }
  }

  private getSaveErrorMessage(error: any): string {
    return error?.error?.message ?? error?.message ?? 'unknown error';
  }

  async recomputeScore() {
    if (!this.game._id) {
      this.alertService.showError(
        'Save the game before recomputing the score.'
      );
      return;
    }
    try {
      const computed = await firstValueFrom(
        this.gameService.recomputeScore(this.game._id)
      );
      this.game.homeGoals = computed.homeGoals;
      this.game.awayGoals = computed.awayGoals;
      this.alertService.showInfo(
        'Score recomputed from goal events. Save the game to persist the new values.'
      );
    } catch (error: any) {
      const message =
        error?.error?.message ??
        error?.message ??
        'Could not recompute score';
      this.alertService.showError(`Recompute failed: ${message}`);
    }
  }

  async recalculatePoints() {
    if (!this.game._id) {
      this.alertService.showError(
        'Save the game before recalculating points.'
      );
      return;
    }
    try {
      const computed = await firstValueFrom(
        this.gameService.recalculatePoints(this.game._id, {
          homeGoals: this.game.homeGoals,
          awayGoals: this.game.awayGoals,
          overtime: !!this.game.overtime,
          shootout: !!this.game.shootout
        })
      );
      this.game.homePoints = computed.homePoints;
      this.game.awayPoints = computed.awayPoints;
      this.alertService.showInfo(
        'Game points recalculated. Save the game to persist the new values.'
      );
    } catch (error: any) {
      const message =
        error?.error?.message ??
        error?.message ??
        'Could not recalculate points';
      this.alertService.showError(`Recalculate failed: ${message}`);
    }
  }

  private checkSihfLiveTrainingGame() {
    if (this.game.isSihfLive && this.game.isTrainingGame) {
      throw new Error(
        'A game with SIHF live export enabled cannot be marked as a training game.'
      );
    }
  }

  private checkCustomLineup() {
    const invalidPlayerInputs = this.customPlayerInputs
      .toArray()
      .filter((input) => input && !input.isValid());
    if (invalidPlayerInputs.length > 0) {
      throw new Error('invalid players in custom lineup');
    }
  }

  private checkPlayerIds() {
    const idPattern = PLAYER_ID_REGEXP;
    const idKeys = Object.keys(this.game).filter((k) => k.endsWith('ID'));
    for (const key of idKeys) {
      const value = this.game[key];
      if (value == null || value === '') {
        continue;
      }
      const str = String(value);
      if (!idPattern.test(str)) {
        throw new Error(`invalid player ID "${str}"`);
      }
    }
  }

  async liveStreamAdded(liveStream: Video) {
    this.gameVideoDetailsService.addLiveStream(this.game, liveStream);
  }

  addVideo(index: number) {
    this.gameVideoDetailsService.addVideo(this.game, index);
  }

  removeVideo(video: Video) {
    this.gameVideoDetailsService.removeVideo(this.game, video);
  }

  videoUrlChanged(video: Video) {
    this.gameVideoDetailsService.updateVideoFormatFromUrl(video);
  }

  async uploadVideo(video?: Video) {
    const uploaded = await this.gameVideoDetailsService.uploadVideo(
      this.game,
      this.leagues,
      video
    );
    if (uploaded) {
      await this.apply();
    }
  }

  /** One-time camera sync; it runs in the background and saves the offsets itself. */
  async syncLiveStreams() {
    this.isSyncingLiveStreams = true;
    try {
      await firstValueFrom(this.videoService.syncLiveStreams(this.game._id));
      this.alertService.showInfo(
        'Camera sync started. The offsets are saved when it is done; reload to see them.'
      );
    } catch (error) {
      this.alertService.showError(
        `Camera sync could not be started: ${
          error.error?.message ?? error.message
        }`
      );
    } finally {
      this.isSyncingLiveStreams = false;
    }
  }

  async downloadVideo(video: Video) {
    await this.gameVideoDetailsService.downloadVideo(this.game, video);
  }

  private checkDuplicates(values: string[], entityType: string) {
    const duplicates = values.filter((p, i) => values.indexOf(p) !== i);
    if (duplicates.length > 0) {
      throw new Error(`duplicate ${entityType} "${duplicates[0]}"`);
    }
  }

  onDataSetChange(value: string) {
    if (value !== DataSet.LITE && value !== DataSet.LITE_PLUS) {
      this.gameStatusOptions = this.gameStatusOptions.filter(
        (item) => item !== GameStatus.IN_EXTENDED_COLLECTION
      );
    } else {
      this.gameStatusOptions = Object.values(GameStatus);
      this.game.autoPuckPossessionEvents = false;
    }
  }

  onGameStatusChange(event: string) {
    switch (event) {
      case GameStatus.NEW:
        this.game.progress = GameProgress.BEFORE_START;
        break;
      case GameStatus.COMPLETE:
        this.game.progress = GameProgress.ENDED;
        break;
    }
  }

  async onStatusUpdated(status: GameStatus): Promise<void> {
    if (status === GameStatus.COMPLETE) {
      await this.dcRosterComponent?.refresh();
    }
  }

  updateFinalizationStatus(status: string) {
    if (this.game.finalization) {
      this.game.finalization.status = status as FinalizationStatus;
    }
  }

  taskId(taskArn: string) {
    return taskArn?.split('/').pop();
  }

  duration(start: Date, stop: Date) {
    if (!start || !stop) {
      return null;
    }
    return (new Date(stop).getTime() - new Date(start).getTime()) / 1000;
  }

  tagsChange(tags: string[]) {
    this.game.tags = tags ?? [];
  }

  isCVError(item: CVPipelineStatus) {
    return item.lastStatus === 'FAILED';
  }

  deleteCVPipelineRecord(item: CVPipelineStatus) {
    const i = this.game.cvPipeline.findIndex(
      (s) =>
        (item.taskArn && s.taskArn === item.taskArn) ||
        (item.jobId && s.jobId === item.jobId)
    );
    this.game.cvPipeline.splice(i, 1);
  }

  cvTaskType(p: CVPipelineStatus) {
    if (p.taskArn) {
      return 'LIVE (ECS)';
    } else if (p.jobId) {
      return 'NON-LIVE (BATCH)';
    }
  }

  findGameOfficial(
    officialRoleInGame: OfficialRoleInGame,
    sequenceNumber: number
  ) {
    const gameOfficial = this.game.officials.find(
      (go: GameOfficial) =>
        go.officialRoleInGame === officialRoleInGame &&
        go.sequenceNumber === sequenceNumber
    );
    return gameOfficial;
  }

  findOfficial(officialRoleInGame: OfficialRoleInGame, sequenceNumber: number) {
    const gameOfficial = this.findGameOfficial(
      officialRoleInGame,
      sequenceNumber
    );
    return gameOfficial
      ? this.officials.find((o) => o.id === gameOfficial.officialId)
      : undefined;
  }

  async updateGameOfficial(
    officialId: number | undefined,
    jerseyNumber: number | undefined,
    role: OfficialRoleInGame,
    sequenceNumber: number
  ) {
    const gameOfficial = this.findGameOfficial(role, sequenceNumber);
    if (gameOfficial) {
      gameOfficial.officialId = officialId;
      gameOfficial.jerseyNumber = jerseyNumber;
      if (officialId) {
        delete gameOfficial.unknownOfficialInfo;
      }
      await this.cleanupGameOfficials();
    } else if (officialId) {
      this.game.officials.push({
        officialRoleInGame: role,
        sequenceNumber,
        officialId,
        jerseyNumber
      });
      await this.cleanupGameOfficials();
    } else if (!officialId) {
      await this.cleanupGameOfficials();
    }
  }

  private async cleanupGameOfficials() {
    this.game.officials = this.game.officials.filter(
      (go) => !!go.officialId || !!go.unknownOfficialInfo
    );
    this.officials = await this.fetchOfficials(this.game.getOfficialIds());
  }

  getCVWarnings(status: CVPipelineStatus) {
    if (!status.warnings) {
      return this.game['cv-pipeline']
        ? this.game['cv-pipeline']['period_' + status.period]?.warnings
        : undefined;
    }
    return status.warnings;
  }

  hasCustomerLineup(team: TeamType): boolean {
    this.validateTeamType(team);
    const customerLineup = this.game?.customerLineup;
    if (!customerLineup) {
      return false;
    }
    const lineup = team === 'home' ? customerLineup.home : customerLineup.away;
    return lineup != null && Object.keys(lineup).length > 0;
  }

  applyCustomerLineup(team: TeamType) {
    this.validateTeamType(team);
    const customerLineup = this.game?.customerLineup;
    if (!customerLineup) {
      return;
    }

    const lineup = team === 'home' ? customerLineup.home : customerLineup.away;
    if (!lineup || Object.keys(lineup).length === 0) {
      this.alertService.showError(
        `No customer lineup available for ${team} team`
      );
      return;
    }

    // Save original lineup for revert
    this.saveOriginalLineup(team);

    // Apply customer lineup - simply copy all fields
    for (const [key, value] of Object.entries(lineup)) {
      this.game[key] = value;
    }

    if (team === 'home') {
      this.homeCustomerLineupApplied = true;
    } else {
      this.awayCustomerLineupApplied = true;
    }

    const shortName = team === 'home' ? this.game.homeTeam : this.game.awayTeam;
    this.alertService.showInfo(`Customer lineup applied for ${shortName}`);
  }

  private saveOriginalLineup(team: TeamType) {
    this.validateTeamType(team);
    const original: Partial<Game> = {};

    // Explicitly list all lineup fields to ensure they're captured
    const goalkeeperPositions = ['Goalkeeper', 'BackupGoalkeeper'];
    // Save goalkeeper fields
    for (const pos of goalkeeperPositions) {
      const key = `${team}${pos}`;
      original[key] = this.game[key];
      original[`${key}ID`] = this.game[`${key}ID`];

      //clear the position in the game object
      this.game[key] = '';
      this.game[`${key}ID`] = '';
    }

    // Save line fields
    for (const line of this.lines) {
      for (const pos of this.linePositions) {
        const key = `${team}${line}${pos}`;
        original[key] = this.game[key];
        original[`${key}ID`] = this.game[`${key}ID`];

        //clear the position in the game object
        this.game[key] = '';
        this.game[`${key}ID`] = '';
      }
    }

    if (team === 'home') {
      this.originalHomeLineup = original;
    } else if (team === 'away') {
      this.originalAwayLineup = original;
    } else {
      throw new Error(`Invalid team type for saving original lineup ${team}`);
    }
  }

  revertLineup(team: TeamType) {
    // validate teamType
    this.validateTeamType(team);
    const original =
      team === 'home' ? this.originalHomeLineup : this.originalAwayLineup;

    if (!original) {
      this.alertService.showError('No original lineup to revert to');
      return;
    }

    for (const [key, value] of Object.entries(original)) {
      this.game[key] = value;
    }

    if (team === 'home') {
      this.homeCustomerLineupApplied = false;
      this.originalHomeLineup = null;
    } else {
      this.awayCustomerLineupApplied = false;
      this.originalAwayLineup = null;
    }

    const shortName = team === 'home' ? this.game.homeTeam : this.game.awayTeam;

    this.alertService.showInfo(`Lineup reverted for ${shortName}`);
  }

  private validateTeamType(team: TeamType) {
    if (team !== 'home' && team !== 'away') {
      throw new Error('Invalid team type for reverting lineup');
    }
  }

  protected readonly Object = Object;
  protected readonly domSanitizer = this.sanitizer;
}
